import HOOK from "../hooks"
import { CATEGORY, COLOR, DATES, FORMATTING, IN_YEAR, JARGON, KINSHIP, POSITION, POSITION_ENUM, ROLE_STATUS, SEX, SEX_ENUM, TENSE, TENSE_ENUM } from "../../modules/constants"
import { CHECKBOX } from "../../modules/checkbox_constants"
import { dateStringUnknown, formatDate, fromDateUnknown, getAge, getCheckboxName, getNameProp, getStatusLevel, getYearsAndMonths, yearsElapsed } from "../../modules/utilities"
import { addPaddingRightToLastCell, getCellWithTooltip, setStats } from "../../modules/table.funcs"

const { border1, black, bold, date, normal, number, right, string } = FORMATTING
const borderLeft = border1

export const getRowFromCapacity = props => {
  // console.log('getRowFromCapacity props:', props)
  const { indexBit, isCompany, item, lseCompany, maxLseData, parentName, portrait, role, showSecondDegree, stats } = props
  const tenseColor = getTenseColor(maxLseData, role)
  const position = getPosition(role, maxLseData)

  role.connectionStart = new Date(role.from)
  // if no resigned date available, set to DATES.FUTURE so that connectionEnd can be identified accordingly (using FUTURE_YEAR) in table.funcs, and used for sorting
  role.connectionEnd = role.until ? new Date(role.until) : DATES.FUTURE

  const cell = {
    //#region get the cells
    ftseIndex: getIndexCell(indexBit, black),
    name: getNameCell(item, position.color),
    role: getRoleCell({ role, color: position.color, parentName, maxLseData, kinship: item.kinship }),
    via: getViaCell(item, JARGON.MORE_INFO),

    start: getStartCell(role, tenseColor),
    end: getEndCell(role, tenseColor),
    overlap: getOverlapCell(role, stats, tenseColor),
    age: item.entity.personInfo
      ? getAgeCell(item, stats)
      : getCellWithTooltip({ borderLeft, tooltip: JARGON.NOT_APPLICABLE, value: JARGON.UNKNOWN }),
    compensation: getCompensationCell(role, stats),
    sex: getSexCell(item, stats),
  }

  const row = getRow({ ...props, cell, position })

  row.cellWithTooltips = getNexusCellWithTooltip({ ...props, cell, position })

  if (!portrait) {
    if (isCompany || showSecondDegree) {
      if (lseCompany && maxLseData) {
        row.cellWithTooltips = [...row.cellWithTooltips, cell.compensation, cell.age, cell.sex]
      } else { // not Lse
        row.cellWithTooltips.push(cell.age)
      }
    }
    // for a person
    if (!isCompany && maxLseData && !showSecondDegree)
      row.cellWithTooltips.push(cell.compensation)
  }

  addPaddingRightToLastCell(row.cellWithTooltips)

  return row
}

const getRow = ({ item, maxLseData, position }) => {
  const companyFontWeight = maxLseData && item.entity?.companyProps?.lse ? bold : normal // set FTSE companies text to bold

  return {
    _id: item.entity._id,
    fontWeight: companyFontWeight === bold || position.fontWeight === bold ? bold : normal,
    icon: item.entity.category,
    iconColor: (maxLseData || item.entity.category === CATEGORY.COMPANY) ? getStatusColor(item) : black,
    index: item.index,
    isFavourite: item.entity.isFavourite,
    link: getLink(item),
  }
}

const getNexusCellWithTooltip = ({ cell, isCompany, portrait, showIndex, showSecondDegree, tense }) => {
  let cellWithTooltips = [cell.name]

  if (portrait)
    cellWithTooltips = showSecondDegree
      ? [...cellWithTooltips, cell.via, cell.overlap]
      : isCompany
        ? [...cellWithTooltips, cell.role, cell.overlap]
        : [...cellWithTooltips, cell.ftseIndex, cell.overlap]
  else {
    if (!showSecondDegree)
      cellWithTooltips = [...cellWithTooltips, cell.role]

    // Add "ftseIndex"
    if (showIndex)
      cellWithTooltips = [...cellWithTooltips, cell.ftseIndex]

    // If showing 2nd degree, add "via.name"
    if (showSecondDegree)
      cellWithTooltips = [...cellWithTooltips, getNameProp.toPresent(cell.via?.name)]

    // If showing 2nd degree, add "start" (appointed)
    cellWithTooltips = [...cellWithTooltips, cell.start]

    // If past or notice, add "start" (appointed)
    if (tense[TENSE_ENUM.PAST] || tense[TENSE_ENUM.NOTICE])
      cellWithTooltips = [...cellWithTooltips, cell.end]

    // Add "overlap"
    cellWithTooltips = [...cellWithTooltips, cell.overlap]
  }

  return cellWithTooltips
}

const getTenseColor = (maxLseData, role) => maxLseData
  ? (role.tense !== null) ? TENSE[role.tense].COLOR : "pink" // corporate officer had no tense - needs attention
  : TENSE[role.tense].COLOR

const getPosition = (role, maxLseData) => {
  const typeBit = getPositionTypeBit(role)
  return {
    typeBit,
    color: POSITION[typeBit].COLOR,
    fontWeight: getPositionFontWeight(typeBit, maxLseData),
  }
}

const getPositionFontWeight = (positionTypeBit, maxLseData) =>
  (maxLseData && positionTypeBit >= 0 && POSITION[positionTypeBit].FONT_WEIGHT)
    ? POSITION[positionTypeBit].FONT_WEIGHT : normal

const getPositionTypeBit = role => role.position?.typebit > 0
  ? role.position.typeBit
  : POSITION_ENUM.OTHER

const getAgeCell = (item, stats) => {
  const value = getAge(item.entity.personInfo.dateOfBirth)
  setStats(value, stats.age)
  // const ageTip = `${age} years old` // - born in ${item.entity.personInfo.dateOfBirth}`
  return getCellWithTooltip({
    borderLeft,
    value,
    sortValue: value === JARGON.UNKNOWN ? 0 : value
  }) // TODO - should be able to find if secretary also has a director role elsewhere, even in past
}

const getStatusColor = item => item.entity.category === CATEGORY.COMPANY
  ? COLOR[getStatusLevel(item.entity.companyProps.status)] // company
  : item.entity.personInfo?.sex == SEX_ENUM.MALE || item.entity.personInfo?.sex == SEX_ENUM.FEMALE // person
    ? SEX[item.entity.personInfo.sex].COLOR
    : SEX[SEX_ENUM.UNKNOWN].COLOR

const getNameCell = (item, color) =>
  getCellWithTooltip({
    color,
    sortValue: getNameProp.toSort(item.entity?.name),
    type: string,
    value: getNameProp.toPresent(item.entity?.name),
  })

const getViaCell = (item, tooltip) =>
  getCellWithTooltip({
    sortValue: getNameProp.toSort(item.via?.name), // If toSort is undefined, value is used
    tooltip,
    type: string,
    value: getNameProp.toPresent(item.via?.name) ?? "",
  })

const getStartCell = (role, color) =>
  getCellWithTooltip({
    borderLeft,
    color,
    sortValue: role.connectionStart
      ? role.connectionStart.getTime()
      : 0,
    tooltip: role.connectionStart && !fromDateUnknown(role.connectionStart)
      ? formatDate(role.connectionStart, true)
      : null,
    type: date,
    value: role.connectionStart,
  })

const getEndCell = (role, color) =>
  getCellWithTooltip({
    tooltip: getEndCellTooltip(role),
    color,
    type: date,
    value: role.connectionEnd,
    sortValue: getEndSortValue(role),
  })

const getEndCellTooltip = role => {
  return role.future
    ? ROLE_STATUS.FUTURE
    : role.connectionEnd && !dateStringUnknown(role.connectionEnd)
      ? formatDate(role.connectionEnd, true)
      : ROLE_STATUS.CURRENT
}

const getEndSortValue = role => {
  return role.connectionEnd
    ? role.connectionEnd.getTime()
    : DATES.FUTURE
}

const getSexCell = (item, stats) => {
  const sex = item.entity.personInfo?.sex ?? SEX_ENUM.UNKNOWN

  if (sex === SEX_ENUM.MALE || sex === SEX_ENUM.FEMALE) {
    stats.sex.total += sex
    ++stats.sex.numof
  }

  return getCellWithTooltip({
    align: right,
    tooltip: SEX[sex].NAME.toLowerCase(),
    type: string,
    value: SEX[sex].SHORT,
  })
}

const getCompensationCell = (role, stats) => {
  const compensation = role.compensationGBP ?? JARGON.UNKNOWN

  setStats(compensation, stats.compensation)

  return getCellWithTooltip({
    align: right,
    sortValue: role.compensationGBP ?? -1,
    type: string,
    value: isNaN(compensation)
      ? JARGON.UNKNOWN
      : compensation.toLocaleString(),
  })
}

const getOverlapCell = (role, stats, color) => {
  const value = role.from ? getYearsAndMonths(role.from, role.until) : JARGON.UNKNOWN
  const years = role.days ? role.days / IN_YEAR.DAYS : JARGON.UNKNOWN // convert days to years
  setStats(years, stats.tenure)

  return getCellWithTooltip({
    color,
    value,
    sortValue: role.days ?? 0,
    prefix: fromDateUnknown(role.connectionStart) ? "at least" : null
  })
}

export const getOverlapCell2 = (item, stats, color) => {
  const overlapDurationStr = item.connectionOverlapDuration ? getYearsAndMonths(item.connectionOverlapStart, item.connectionOverlapEnd) : JARGON.UNKNOWN
  const overlapDuration = item.connectionOverlapDuration ? yearsElapsed(item.connectionOverlapStart, item.connectionOverlapEnd) : 0
  setStats(overlapDuration, stats.tenure, true)

  return getCellWithTooltip({
    color,
    prefix: fromDateUnknown(item.connectionStart) ? "at least" : null,
    sortValue: overlapDuration,
    type: number,
    value: overlapDurationStr,
  })
}

const getRoleCell = ({ role, color, parentName, maxLseData, kinship }) => {
  // const tooltip = maxLseData ? role.position.title : role.roleDescription // ? role.roles.join(", ") : ""
  const prefix = getRolePrefix({ kinship, psc: role.psc, tense: role.tense, parentName })

  return getCellWithTooltip({
    color,
    type: string,
    value: prefix + (maxLseData ? role.position.title : role.roleDescription)
  }) // HIGHLIGHT MISMATCHES
}

export const getRolePrefix = ({ kinship, psc, tense, parentName }) => !!!psc
  ? ""
  : kinship === KINSHIP.CHILD
    ? tense === TENSE_ENUM.PAST
      ? `was a PSC of ${parentName}: `
      : `is a PSC of ${parentName}: `
    : tense === TENSE_ENUM.PAST
      ? `had ${parentName} as a PSC: `
      : `has ${parentName} as a PSC: `

export const getRoleFromBasics = ({ roleDescription, item }) => {
  return {
    from: item.connectionStart,
    position: {
      title: roleDescription,
      type: item.position?.type,
      typeBit: item.position?.typeBits,
    },
    psc: !!(item.entity.position.typeBits & POSITION_ENUM.PSC),
    roleDescription,
    tense: item.tense,
    until: item.connectionEnd,
  }
}

const getRoleCellFromBasics = ({ roleDescription, color, parentName, item }) => {
  // console.log('item.entity.position:', item.entity.position)
  return getCellWithTooltip({
    color,
    type: string,
    value: !!(item.entity.position.typeBits & POSITION_ENUM.PSC)
      ? item.entity.position.tense === TENSE_ENUM.PAST
        ? `had ${parentName} as a PSC: ${roleDescription}`
        : `has ${parentName} as a PSC: ${roleDescription}`
      : item.entity.position.tense === TENSE_ENUM.PAST
        ? `had ${parentName} as ${roleDescription}`
        : `has ${parentName} as ${roleDescription}`
  })
}

export const getIndexCell = (indexBit, color) => {
  const ftseIndex = indexBit ? getCheckboxName(CHECKBOX[HOOK.LSE_INDEX], indexBit) : JARGON.OTHER.toLowerCase()
  return getCellWithTooltip({
    color,
    sortValue: indexBit || 99,
    type: string,
    value: ftseIndex,
  }) // HIGHLIGHT MISMATCHES
}

const getLink = item => `/${item.entity.category || item.category.keyValues["category"]}/${item.entity.ids.join(",")}`

export const getRowFromBasics = props => {
  // no connection - 'is psc of' or second degree
  // console.log('getRowFromBasics props:',props)
  const { isCompany, item, lseCompany, maxLseData, portrait, showSecondDegree, parentName, roleDescription, stats, tense } = props
  const showEndColumn = tense[TENSE_ENUM.PAST] || tense[TENSE_ENUM.NOTICE]
  const companyFontWeight = maxLseData && (item.entity.companyProps?.lse || item?.via?.indexBit)
    ? bold
    : normal // set FTSE companies text to bold
  // console.log('getRowFromBasics roleDescription:', roleDescription)

  const itemTense = item.tense ?? item.role.tense
  const tenseColor = maxLseData
    ? itemTense !== undefined ? TENSE[itemTense].COLOR : "pink" // corporate officer had no tense - needs attention
    : TENSE[TENSE_ENUM.CURRENT].COLOR

  const statusColor = getStatusColor(item)
  const positionTypeBit = getPositionTypeBit(item)
  const positionColor = POSITION[positionTypeBit].COLOR
  const positionFontWeight = getPositionFontWeight(positionTypeBit, maxLseData)

  //#region  get the cells
  const nameCell = getNameCell(item, positionColor)
  const roleCell = getRoleCellFromBasics({ roleDescription, color: positionColor, parentName, item })
  const viaNodeCell = getViaCell(item)
  const startCell = getStartCell(item, tenseColor)
  // Set to DATES.FUTURE so that connectionEnd can be identified accordingly (using FUTURE_YEAR) in table.funcs
  item.connectionEnd = item.future ? DATES.FUTURE : item.connectionEnd
  const endCell = getEndCell(item, tenseColor)
  const overlapCell = getOverlapCell2(item, stats, tenseColor)
  const ageCell = item.entity.personInfo
    ? getAgeCell(item, stats) // getAgeCell(item, rolesString, stats)
    : getCellWithTooltip({ borderLeft, tooltip: JARGON.NOT_APPLICABLE, value: JARGON.UNKNOWN })
  const compensationCell = getCompensationCell(item, stats)
  const sexCell = getSexCell(item, stats)
  const indexCell = isCompany
    ? getIndexCell(item.entity.companyProps?.lse?.indexBit, black)
    : getIndexCell(item?.via?.indexBit, black)
  //#endregion get the cells

  let row =
  {
    _id: item.entity._id,
    index: item.index,
    link: getLink(item),
    icon: item.entity.category,
    iconColor: statusColor,
    fontWeight: companyFontWeight === bold || positionFontWeight === bold ? bold : normal,
    isFavourite: item.entity.isFavourite,

    cellWithTooltips: portrait
      ? isCompany
        ? showSecondDegree
          ? [nameCell, viaNodeCell, overlapCell]
          : [nameCell, roleCell, overlapCell]
        : showSecondDegree
          ? [nameCell, viaNodeCell, overlapCell]
          : [nameCell, overlapCell]
      : isCompany
        ? showSecondDegree
          ? showEndColumn
            ? maxLseData
              ? [nameCell, indexCell, viaNodeCell, startCell, endCell, overlapCell] // current & past, second degree - OK
              : [nameCell, viaNodeCell, startCell, endCell, overlapCell] // current & past, second degree - OK
            : maxLseData
              ? [nameCell, indexCell, viaNodeCell, startCell, overlapCell] // current, second degree
              : [nameCell, viaNodeCell, startCell, overlapCell] // current, second degree
          : showEndColumn
            ? [nameCell, roleCell, startCell, endCell, overlapCell] // current & past, first degree only - OK
            : [nameCell, roleCell, startCell, overlapCell] // current, first degree only - OK
        : showSecondDegree
          ? showEndColumn
            ? maxLseData
              ? [nameCell, viaNodeCell, indexCell, startCell, endCell, overlapCell] // current & past, second degree - OK
              : [nameCell, viaNodeCell, startCell, endCell, overlapCell] // current & past, second degree - OK
            : maxLseData
              ? [nameCell, viaNodeCell, indexCell, startCell, overlapCell] // current, second degree
              : [nameCell, viaNodeCell, startCell, overlapCell] // current, second degree
          : showEndColumn
            ? [nameCell, roleCell, startCell, endCell, overlapCell] // current & past, first degree only - OK
            : [nameCell, roleCell, startCell, overlapCell], // current, first degree only  - OK
  }
  if (!portrait && isCompany && !showSecondDegree) {
    if (lseCompany && maxLseData) {
      row.cellWithTooltips = [...row.cellWithTooltips, compensationCell, ageCell, sexCell]
    } else { // not Lse
      row.cellWithTooltips.push(ageCell)
    }
  }

  addPaddingRightToLastCell(row.cellWithTooltips)

  return row
}